import { useState } from 'react';
import { useAdjustProduct, useRestockProduct } from '../api/inventory';
import type { InventoryStateRow } from '../api/inventory';
import { ApiError } from '../api/client';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';

export interface InventoryRestockModalProps {
  eventId: string;
  product: InventoryStateRow;
  mode: 'restock' | 'adjust';
  onClose: () => void;
}

export function InventoryRestockModal({
  eventId,
  product,
  mode,
  onClose,
}: InventoryRestockModalProps) {
  const restock = useRestockProduct(eventId);
  const adjust = useAdjustProduct(eventId);
  const [delta, setDelta] = useState<string>(mode === 'restock' ? '1' : '');
  const [remaining, setRemaining] = useState<string>(
    product.inventoryRemaining != null ? String(product.inventoryRemaining) : '',
  );
  const [note, setNote] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      if (mode === 'restock') {
        const n = Number.parseInt(delta, 10);
        if (!Number.isFinite(n) || n <= 0) {
          setError('Enter a positive integer.');
          return;
        }
        await restock.mutateAsync({
          productId: product.productId,
          delta: n,
          note: note || undefined,
        });
      } else {
        const n = Number.parseInt(remaining, 10);
        if (!Number.isFinite(n) || n < 0) {
          setError('Enter a non-negative integer.');
          return;
        }
        await adjust.mutateAsync({
          productId: product.productId,
          remaining: n,
          note: note || undefined,
        });
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'exceeds_starting') {
        setError(
          `Remaining can't be more than the starting count (${product.inventoryStarting ?? 0}). Use Restock to add units.`,
        );
      } else if (err instanceof ApiError && err.code === 'inventory_not_tracked') {
        setError('This product no longer tracks inventory. Close and reload the list.');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Try again.');
      }
    }
  };

  const title = mode === 'restock' ? 'Restock' : 'Adjust stock';

  return (
    <Modal
      title={title}
      sub={product.name}
      width={420}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()}>
            {mode === 'restock' ? 'Restock' : 'Adjust'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>
          Currently {product.inventoryRemaining ?? 0} of {product.inventoryStarting ?? 0} remaining.
        </div>
        {mode === 'restock' ? (
          <Field
            label="Add to remaining"
            help="Adds the units to both starting and remaining."
          >
            <input
              type="number"
              min={1}
              step={1}
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              data-testid="restock-delta"
            />
          </Field>
        ) : (
          <Field label="Set remaining to" help="Use after a physical recount.">
            <input
              type="number"
              min={0}
              step={1}
              value={remaining}
              onChange={(e) => setRemaining(e.target.value)}
              data-testid="adjust-remaining"
            />
          </Field>
        )}
        <Field label="Note (optional)">
          <input
            type="text"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        {error && <div className="form-error">{error}</div>}
      </div>
    </Modal>
  );
}
